import { useCallback, useEffect, useState } from "react";

import { api, SessionExpired } from "./api";
import { currentUser, resume, signIn, signOut } from "./auth";
import { Icon } from "./components/Icon";
import { ManualAddDialog } from "./components/ManualAddDialog";
import type { Client } from "./types";
import { ActionsView } from "./views/ActionsView";
import { ClientPage } from "./views/ClientPage";
import { ClientsView } from "./views/ClientsView";
import { LogView } from "./views/LogView";
import { ReportsView } from "./views/ReportsView";
import { ReviewQueue } from "./views/ReviewQueue";
import { SourcesView } from "./views/SourcesView";

type Tab = "queue" | "actions" | "log" | "reports" | "clients" | "sources";

/**
 * The one route this app has: `#/clients/<id>`, a client popped out into its own tab.
 *
 * A hash rather than a path, deliberately. CloudFront serves a single index.html from S3,
 * so a real path would need a rewrite rule to avoid a 404 on reload — and a routing rule
 * that only breaks on refresh is one nobody notices until she refreshes.
 */
function routedClient(): string | null {
  const match = /^#\/clients\/([A-Za-z0-9-]+)$/.exec(window.location.hash);
  return match ? match[1] : null;
}

/**
 * `short` is the label under the icon in the phone's bottom bar, where "Review queue" does not
 * fit. `phone: false` tabs live behind More on a phone: the bar has room for five, and Clients
 * and Sources are configuration she visits, not work she does on the move.
 */
const TABS: { id: Tab; label: string; short?: string; phone: boolean }[] = [
  { id: "queue", label: "Review queue", short: "Queue", phone: true },
  // Second, not last: what is overdue matters more than what is new, and a tab nobody
  // walks past is a tab nobody opens.
  { id: "actions", label: "Actions", phone: true },
  { id: "log", label: "Log", phone: true },
  // Beside the log, because that is what it reads from: a board paper is the log written up
  // for one client and one month. Endpoints existed for both of these before buttons did,
  // and a capability nobody can find is a capability nobody has.
  { id: "reports", label: "Reports", phone: true },
  { id: "clients", label: "Clients", phone: false },
  { id: "sources", label: "Sources", phone: false },
];

export function App() {
  const [state, setState] = useState<"checking" | "in" | "out">("checking");
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("queue");
  const [clients, setClients] = useState<Client[]>([]);
  const [queueCount, setQueueCount] = useState<number | null>(null);
  const [pendingSubscriptions, setPendingSubscriptions] = useState<number>(0);
  const [actionsCount, setActionsCount] = useState<number>(0);
  const [adding, setAdding] = useState(false);
  /** The phone's More sheet: the tabs and account controls that do not fit the bottom bar. */
  const [more, setMore] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [routed, setRouted] = useState<string | null>(routedClient);

  useEffect(() => {
    const onHashChange = () => setRouted(routedClient());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (!more) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setMore(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [more]);

  useEffect(() => {
    resume()
      .then((session) => setState(session ? "in" : "out"))
      .catch((err: Error) => {
        setError(err.message);
        setState("out");
      });
  }, []);

  // The client list drives the tag pickers everywhere, so it is loaded once and shared.
  const loadClients = useCallback(() => {
    api
      .listClients()
      .then((page) => setClients(page.items))
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (state === "in") loadClients();
  }, [state, loadClients]);

  // A change to her feeds is not something she should have to go looking for, so the
  // count is fetched on sign-in rather than only when the Sources tab is opened.
  useEffect(() => {
    if (state !== "in") return;
    api
      .listSubscriptions("pending")
      .then((page) => setPendingSubscriptions(page.count))
      .catch(() => setPendingSubscriptions(0));
  }, [state, refreshKey]);

  // Same reasoning for work she has committed to. An overdue action is the one thing here
  // with a deadline attached to it, and a tab she has to open to discover that is a tab
  // that will be opened too late. ActionsView keeps this in step once she is on it.
  useEffect(() => {
    if (state !== "in") return;
    api
      .listActions()
      .then((page) => setActionsCount(page.count))
      .catch(() => setActionsCount(0));
  }, [state, refreshKey]);

  /** A session that runs out mid-use is not an error worth alarming her about. */
  const handleError = useCallback((err: unknown) => {
    if (err instanceof SessionExpired) {
      setState("out");
      return;
    }
    setError(err instanceof Error ? err.message : String(err));
  }, []);

  /** A new tab starts at its top, not at the scroll position the last one was left at. */
  const selectTab = (next: Tab) => {
    setTab(next);
    // The document scrolls on a phone and `main` scrolls on a desk; reset whichever it is.
    window.scrollTo({ top: 0 });
    document.querySelector("main")?.scrollTo({ top: 0 });
  };

  const badgeFor = (id: Tab): number | null =>
    id === "queue"
      ? queueCount
      : id === "actions"
        ? actionsCount
        : id === "sources"
          ? pendingSubscriptions
          : null;

  /** On a phone, More stands in for the tabs it holds, so it reads as selected when one is. */
  const moreActive = TABS.some((entry) => !entry.phone && entry.id === tab);

  if (state === "checking") {
    return <div className="centred muted">Checking your sign-in…</div>;
  }

  if (state === "out") {
    return (
      <div className="centred">
        <div className="signin">
          <div className="mark">
            <img src="/norwood-logo.png" alt="Norwood Risk & Compliance" />
          </div>
          <h1>Regulatory Change</h1>
          {error && <p className="error">{error}</p>}
          <button className="primary" onClick={() => void signIn()}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  if (routed) return <ClientPage clientId={routed} />;

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <img src="/norwood-logo.png" alt="Norwood Risk & Compliance" />
          <span className="title">Regulatory Change</span>
        </div>
        {/* One nav for both layouts. On a desk it is the row of words in the purple bar; on a
            phone the stylesheet pins it to the bottom of the screen as an iOS tab bar, within
            reach of a thumb, and the tabs marked `phone: false` move behind More. */}
        <nav className="app-nav" aria-label="Sections">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              className={[
                "tab",
                tab === entry.id ? "active" : "",
                entry.phone ? "" : "desk-only",
              ].join(" ").trim()}
              aria-current={tab === entry.id ? "page" : undefined}
              onClick={() => selectTab(entry.id)}
            >
              <Icon name={entry.id} />
              <span className="tab-label">
                <span className="long">{entry.label}</span>
                <span className="short">{entry.short ?? entry.label}</span>
              </span>
              {badgeFor(entry.id) ? <span className="badge">{badgeFor(entry.id)}</span> : null}
            </button>
          ))}
          <button
            className={moreActive ? "tab more-tab active" : "tab more-tab"}
            aria-haspopup="dialog"
            aria-expanded={more}
            onClick={() => setMore(true)}
          >
            <Icon name="more" />
            <span className="tab-label">More</span>
            {pendingSubscriptions ? <span className="badge">{pendingSubscriptions}</span> : null}
          </button>
        </nav>
        <div className="account">
          <button onClick={() => setAdding(true)}>Add item</button>
          <span className="muted">{currentUser()}</span>
          <button className="link" onClick={signOut}>
            Sign out
          </button>
        </div>
        {/* The phone's one header control: adding an item is the thing she does from a link
            someone sent her, so it stays one tap away rather than inside More. */}
        <button className="header-add" aria-label="Add item" onClick={() => setAdding(true)}>
          <Icon name="plus" />
        </button>
      </header>

      {error && (
        <div className="banner error">
          {error}
          <button className="link" onClick={() => setError(null)}>
            dismiss
          </button>
        </div>
      )}

      <main key={refreshKey}>
        {tab === "queue" && (
          <ReviewQueue clients={clients} onError={handleError} onCountChange={setQueueCount} />
        )}
        {tab === "actions" && (
          <ActionsView
            clients={clients}
            onError={handleError}
            onCountChange={setActionsCount}
          />
        )}
        {tab === "log" && <LogView clients={clients} onError={handleError} />}
        {tab === "reports" && <ReportsView clients={clients} onError={handleError} />}
        {tab === "clients" && <ClientsView clients={clients} onChanged={loadClients} onError={handleError} />}
        {tab === "sources" && (
          <SourcesView onError={handleError} onPendingChange={setPendingSubscriptions} />
        )}
      </main>

      {more && (
        <div className="sheet-backdrop" onClick={() => setMore(false)}>
          <div
            className="sheet more-sheet"
            role="dialog"
            aria-label="More"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet-grabber" aria-hidden="true" />
            <ul className="sheet-list">
              {TABS.filter((entry) => !entry.phone).map((entry) => (
                <li key={entry.id}>
                  <button
                    className={tab === entry.id ? "active" : ""}
                    onClick={() => {
                      selectTab(entry.id);
                      setMore(false);
                    }}
                  >
                    <Icon name={entry.id} />
                    <span>{entry.label}</span>
                    {badgeFor(entry.id) ? (
                      <span className="badge">{badgeFor(entry.id)}</span>
                    ) : null}
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={() => {
                    setMore(false);
                    setAdding(true);
                  }}
                >
                  <Icon name="plus" />
                  <span>Add item</span>
                </button>
              </li>
            </ul>
            <div className="sheet-foot">
              <span className="muted small">Signed in as {currentUser()}</span>
              <button onClick={signOut}>Sign out</button>
            </div>
          </div>
        </div>
      )}

      {adding && (
        <ManualAddDialog
          onError={handleError}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            setRefreshKey((key) => key + 1);
          }}
        />
      )}
    </div>
  );
}
